import { useQuery } from '@tanstack/react-query';
import type { CSSProperties, ReactNode } from 'react';

import type { StorefrontProduct, StorefrontProductSummary } from '@/features/storefront/api/storefront';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import { useAddToCart } from '@/features/storefront/hooks/useCart';
import type { ProductDetailQueryFactory } from '@/features/storefront/sections/LookbookSection';
import type { ProductSpotlightSection } from '@/features/storefront/sections/schema';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';

/**
 * One product shown large: a big picture on one side, and its name, price,
 * a line about it and the buttons on the other.
 *
 * The product is the one the merchant picked, by slug - or, until they pick
 * one, the shop's newest, so the section shows something real the moment it
 * is added. The picture is the product's own unless the merchant gave the
 * section a bigger one of its own (a styled scene of the same product).
 *
 * Add to cart is offered for a product sold in one form only, in stock, and
 * only where the shop has a cart; anything with choices sends the shopper to
 * its page to make them. Elsewhere the card links only.
 */

/** What the spotlight shows of its product. */
export interface SpotlightProduct {
  slug: string;
  name: string;
  price: string;
  compareAtPrice: string | null;
  description: string | null;
  imageUrl: string | null;
  imageFocal: StorefrontProductSummary['image_focal'] | null;
  /** The one variant an add would put in the cart; null for a product with choices. */
  variantId: string | null;
  inStock: boolean;
}

export function spotlightFromSummary(product: StorefrontProductSummary): SpotlightProduct {
  return {
    slug: product.slug,
    name: product.name,
    price: product.price,
    compareAtPrice: product.compare_at_price,
    description: product.short_description,
    imageUrl: product.image_url,
    imageFocal: product.image_focal ?? null,
    variantId: null,
    inStock: product.in_stock,
  };
}

export function spotlightFromDetail(product: StorefrontProduct): SpotlightProduct {
  const variants = product.variants;
  const single = variants.length === 1 ? variants[0] : null;
  return {
    slug: product.slug,
    name: product.name,
    price: product.price,
    compareAtPrice: product.compare_at_price,
    description: product.short_description,
    imageUrl: product.images[0]?.url ?? null,
    imageFocal: product.images[0]?.image_focal ?? null,
    variantId: single && single.in_stock ? single.id : null,
    inStock: variants.length === 0 ? true : variants.some((variant) => variant.in_stock),
  };
}

/** The slug the section shows: the picked one, else the newest product's. */
export function spotlightSlug(
  section: Pick<ProductSpotlightSection, 'product'>,
  summaries: readonly StorefrontProductSummary[],
): string | null {
  const picked = section.product.trim();
  if (picked) return picked;
  return summaries[0]?.slug ?? null;
}

export interface ProductSpotlightViewProps {
  section: ProductSpotlightSection;
  product: SpotlightProduct | null;
  productHref: (slug: string) => string;
  /** A link the way the renderer draws one: plain text in the customiser. */
  link: (href: string, className: string, children: ReactNode, style?: CSSProperties) => ReactNode;
  /** The eyebrow, drawn by the renderer so it can be typed into. */
  eyebrow?: ReactNode;
  /** Puts the one variant in the cart. Unset: the buttons link only. */
  onAdd?: (variantId: string) => void;
  adding?: boolean;
  added?: boolean;
  /** Shown in the customiser while there is no product to show. */
  emptyHint?: ReactNode;
}

const OUTLINE_BUTTON: CSSProperties = {
  backgroundColor: 'transparent',
  color: 'var(--storefront-primary)',
  border: '1px solid var(--storefront-primary)',
  borderRadius: 'var(--storefront-radius)',
};

export function ProductSpotlightView({
  section,
  product,
  productHref,
  link,
  eyebrow,
  onAdd,
  adding,
  added,
  emptyHint,
}: ProductSpotlightViewProps) {
  const copy = useShopContent();
  if (!product) return <>{emptyHint ?? null}</>;

  const picture = section.image_url || product.imageUrl;
  const focal = section.image_url ? section.image_focal : product.imageFocal;
  const href = productHref(product.slug);
  const onSale =
    product.compareAtPrice !== null && Number(product.compareAtPrice) > Number(product.price);
  const canAdd = Boolean(onAdd && product.variantId && product.inStock);
  const button = 'inline-flex h-12 items-center justify-center px-7 text-sm font-semibold';

  return (
    <div
      data-spotlight={section.image_side}
      className="grid items-center gap-8 md:grid-cols-2 md:gap-12"
    >
      <div className={cn('relative', section.image_side === 'left' ? 'md:order-1' : 'md:order-2')}>
        {picture ? (
          <StorefrontImage
            url={picture}
            sizes="(min-width: 768px) 50vw, 100vw"
            alt={product.name}
            className="aspect-square w-full object-cover"
            style={{ borderRadius: 'var(--storefront-radius)', ...focalStyle(focal) }}
          />
        ) : (
          <div
            aria-hidden
            className="aspect-square w-full"
            style={{ borderRadius: 'var(--storefront-radius)', backgroundColor: 'var(--storefront-surface)' }}
          />
        )}
      </div>
      <div className={cn(section.image_side === 'left' ? 'md:order-2' : 'md:order-1')}>
        {eyebrow ? (
          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--storefront-primary)' }}>
            {eyebrow}
          </p>
        ) : null}
        <h2 className="mt-2 text-3xl tracking-tight sm:text-4xl" style={STOREFRONT_HEADING_STYLE}>
          {link(href, '', product.name)}
        </h2>
        <p className="mt-4 flex items-baseline gap-3 text-xl">
          <span className="font-semibold">{formatMoney(product.price)}</span>
          {onSale ? (
            <span className="text-base line-through" style={{ color: 'var(--storefront-muted)' }}>
              {formatMoney(product.compareAtPrice)}
            </span>
          ) : null}
        </p>
        {section.show_description && product.description?.trim() ? (
          <p className="mt-4 text-base leading-relaxed" style={{ color: 'var(--storefront-muted)' }}>
            {product.description}
          </p>
        ) : null}
        <div className="mt-8 flex flex-wrap gap-3">
          {canAdd && product.variantId ? (
            <button
              type="button"
              disabled={adding}
              onClick={() => onAdd?.(product.variantId as string)}
              className={cn(button, 'disabled:opacity-60')}
              style={STOREFRONT_BUTTON_STYLE}
            >
              {added ? copy('account.added_to_cart') : copy('account.add_to_cart')}
            </button>
          ) : null}
          {link(
            href,
            button,
            canAdd ? copy('section.spotlight_view') : copy('section.spotlight_choose'),
            canAdd ? OUTLINE_BUTTON : STOREFRONT_BUTTON_STYLE,
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The spotlight with its product fetched in full - so it knows whether there
 * is one variant to add - falling back to the catalog summary while it loads.
 */
export function FetchedProductSpotlight({
  query,
  slug,
  summary,
  quickAdd,
  ...props
}: Omit<ProductSpotlightViewProps, 'product' | 'onAdd' | 'adding' | 'added'> & {
  query: ProductDetailQueryFactory;
  slug: string;
  summary: SpotlightProduct | null;
  /** Whether the shop has a cart to add to. */
  quickAdd: boolean;
}) {
  const detail = useQuery({ ...query(slug), retry: false, staleTime: 60_000 });
  const add = useAddToCart();
  const product = detail.data ? spotlightFromDetail(detail.data) : summary;
  return (
    <ProductSpotlightView
      {...props}
      product={product}
      onAdd={quickAdd ? (variantId) => add.mutate({ variantId }) : undefined}
      adding={add.isPending}
      added={add.isSuccess}
    />
  );
}
